































































import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  PropType,
} from '@vue/composition-api';
import {
  Button, Checkbox, Dialog,
} from '@hb/coffee';
import ChannelContractTabs from '@/widgets/contract-view/channel-contract-tabs/index.vue';
import type { ContractListResponse } from '@/widgets/contract-view/type';
import { creditMallApply } from '@/api/rights/shop-mall';
import { dcCreditSceneEnum } from '@/api/preApplyChoose/types';
import { VerifySceneEnum, OpenVerifyPageMode } from '@/shared/verify-credit-controller/types';
import jumpToVerifyCredit from '@/shared/verify-credit-controller';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { RouterV2Enum } from '@/router/router-names';
import { ubtSendError } from '@/shared/utils/ubt';

export default defineComponent({
  name: 'BothContract',
  components: {
    ChannelContractTabs,
    Button,
    Checkbox,
  },
  props: {
    /** 分期商城渠道协议列表 */
    fqChannelList: {
      type: Array as PropType<ContractListResponse[]>,
      required: true,
    },
    /** 贷超渠道协议列表 */
    dcChannelList: {
      type: Array as PropType<ContractListResponse[]>,
      required: true,
    },
    /** 投放来源 */
    from: {
      type: String,
      required: true,
    },
    /** 贷超授信场景 */
    dcCreditScene: {
      type: String as PropType<dcCreditSceneEnum>,
      required: true,
    },
    /** 申请模式 */
    applyMode: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const router = context.root.$router;

    /** 倒计时秒数 */
    const countdown = ref(5);
    /** 倒计时定时器 */
    let countdownTimer: number | null = null;

    /** 借款优选协议是否勾选 */
    const isDcCheckboxChecked = ref(false);

    /** 是否已显示过挽留弹窗（防止重复显示） */
    const hasShownRetentionDialog = ref(false);

    /** 是否需要弹出资料补充页弹窗 */
    const needShowVerifyCreditPopup = ref(false);

    /** 授信单号 */
    const currentCreditTaskId = ref('');

    /** 分期商城产品ID列表 */
    const fqProductIds = computed(
      () => props.fqChannelList.map((item) => item.productId),
    );

    /** 贷超渠道ID列表 */
    const dcProductIds = computed(
      () => props.dcChannelList.map((item) => item.productId),
    );

    /** 处理渠道切换 - 内部处理，不向外抛出 */
    const handleChangeChannel = (payload: any) => {
      // 可以在这里添加埋点等逻辑
    };

    /** 处理协议切换 - 内部处理，不向外抛出 */
    const handleChangeContract = (payload: any) => {
      // 可以在这里添加埋点等逻辑
    };

    /** 按钮是否禁用 */
    const isButtonDisabled = computed(() => countdown.value > 0);

    /** 按钮文案 */
    const buttonText = computed(() => {
      if (countdown.value > 0) {
        return `已阅读并同意协议（${countdown.value}s）`;
      }
      return '已阅读并同意协议';
    });

    /** 跳转参数 */
    const jumpQuery = computed<Dictionary<string | boolean | undefined>>(() => ({
      isCheckDcContracts: String(isDcCheckboxChecked.value),
      from: props.from,
      dcIdList: String(dcProductIds.value.join(',')),
      dcCreditScene: props.dcCreditScene,
      applyMode: props.applyMode,
    }));

    /**
     * 启动倒计时
     */
    const startCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      countdown.value = 5;
      countdownTimer = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
        }
      }, 1000);
    };

    /**
     * 切换借款优选协议勾选状态
     */
    const changeDcCheckbox = () => {
      isDcCheckboxChecked.value = !isDcCheckboxChecked.value;
      // 可以在这里添加埋点等逻辑
    };

    /**
     * 跳转到等待页
     */
    const jumpToWaitingPage = () => {
      router.replace({
        path: RouterV2Enum.unitWaitingPage,
        query: {
          creditTaskId: currentCreditTaskId.value,
          ...jumpQuery.value,
        },
      });
    };

    /**
     * 跳转到失败页
     */
    const jumpToFailedPage = (errorMessage: string) => {
      jumpToNewVersion(NewVersionEnum.HALF_PROCESS_FAILED, true);
      ubtSendError(errorMessage);
    };

    /**
     * 显示挽留弹窗
     */
    const showRetentionDialogIfNeeded = (): boolean => {
      // 如果未勾选贷超协议且未显示过挽留弹窗，则显示
      if (!isDcCheckboxChecked.value && !hasShownRetentionDialog.value) {
        hasShownRetentionDialog.value = true;

        // 使用 Dialog.alert 显示挽留弹窗（需要取消按钮以继续流程）
        Dialog.alert({
          title: '借款优选协议',
          message: '为您推荐更多优选机构，同意协议有机会获得更多额度',
          confirmButtonText: '确认',
          onEvent: (eventName) => {
            if (eventName === 'closeIconClick') {
              // eslint-disable-next-line no-use-before-define
              executeApplyLogic();
            }
            if (eventName === 'confirm') {
              isDcCheckboxChecked.value = true;
            }
          },
        });

        return true;
      }
      return false;
    };

    /**
     * 检查贷超协议并处理挽留弹窗或跳转
     */
    const checkDcContractAndHandle = () => {
      // 如果显示了挽留弹窗，则不执行后续跳转
      if (showRetentionDialogIfNeeded()) {
        return;
      }
      // 此时授信成功，直接跳转到等待页面
      jumpToWaitingPage();
    };

    /**
     * 处理贷超流程或失败页跳转
     */
    const handleDcOrFailed = (errorMessage: string) => {
      // 如果显示了挽留弹窗，则不执行后续跳转
      if (showRetentionDialogIfNeeded()) {
        return;
      }
      // 如果勾选了贷超协议，跳转到等待页（贷超流程没有授信单号）
      if (isDcCheckboxChecked.value) {
        jumpToWaitingPage();
      } else {
        // 未勾选贷超协议，跳转到失败页
        jumpToFailedPage(errorMessage);
      }
    };

    /**
     * 跳转到资料补充页
     */
    const jumpToVerifyCreditPage = () => {
      /** 重置需要弹出资料补充页的标记 */
      needShowVerifyCreditPopup.value = false;
      jumpToVerifyCredit(VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING, {
        openMode: OpenVerifyPageMode.POPUP_SHOW,
        successCb: async () => {
          // eslint-disable-next-line no-use-before-define
          executeApplyLogic();
        },
      });
    };

    /**
     * 执行申请逻辑的核心函数
     */
    const executeApplyLogic = async () => {
      // 如果需要弹出资料补充页，直接弹出资料补充页
      if (needShowVerifyCreditPopup.value) {
        jumpToVerifyCreditPage();
        return;
      }

      try {
        /** 未申请过授信，则申请授信 */
        if (!currentCreditTaskId.value) {
          const result = await creditMallApply({
            from: props.from,
            productIds: fqProductIds.value,
          });
          if (result?.creditTaskId) {
            currentCreditTaskId.value = result.creditTaskId;
          }
        }
        // 如果申请授信成功
        if (currentCreditTaskId.value) {
          checkDcContractAndHandle();
        } else {
          // 授信成功但未返回 creditTaskId，根据是否勾选贷超协议决定跳转
          handleDcOrFailed('creditMallApply success but no creditTaskId');
        }
      } catch (error) {
        // 如果申请授信失败并且错误码为300002，需要补充资料
        if ((error as Dictionary).code === 300002) {
          // 设置需要弹出资料补充页的标记
          needShowVerifyCreditPopup.value = true;
          // 先检查是否需要显示挽留弹窗
          if (showRetentionDialogIfNeeded()) {
            return;
          }
          // 弹出资料补充页
          jumpToVerifyCreditPage();
        } else {
          // 其他错误，根据是否勾选贷超协议决定跳转
          handleDcOrFailed('creditMallApply error');
        }
      }
    };

    /**
     * 处理申请按钮点击
     */
    const handleApply = () => {
      executeApplyLogic();
    };

    onMounted(() => {
      startCountdown();
    });

    onBeforeUnmount(() => {
      // 清理定时器
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    });

    return {
      isButtonDisabled,
      buttonText,
      isDcCheckboxChecked,
      handleChangeChannel,
      handleChangeContract,
      handleApply,
      changeDcCheckbox,
    };
  },
});
